/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import { Platform, View } from 'react-native'
import styled, { DefaultTheme } from 'styled-components/native'

import { InputSize, TextInputContainerBaseProps } from 'ui/designSystem/TextInput/types'
import { getSpacing, padding } from 'ui/theme'

const SIZE_TO_HEIGHT = (theme: DefaultTheme) => ({
  small: theme.inputs.height.small,
  regular: theme.inputs.height.regular,
  tall: theme.inputs.height.tall,
})

const BORDER_FOCUS_INCREASE = 0.25

export const TextInputContainerBase: FunctionComponent<TextInputContainerBaseProps> = ({
  children,
  inputSize = 'small',
  isDisabled = false,
  isError = false,
  isFocused = false,
  isHover = false,
  style,
  ...props
}) => (
  <StyledView
    testID="styled-input-container"
    size={inputSize}
    isFocused={isFocused}
    isError={isError}
    isDisabled={isDisabled}
    isHover={isHover}
    style={style}
    {...props}>
    {children}
  </StyledView>
)

const getBorderColor = (
  theme: DefaultTheme,
  isFocused?: boolean,
  isDisabled?: boolean,
  isError?: boolean
) => {
  if (isDisabled) {
    return theme.designSystem.color.border.disabled
  }
  if (isFocused) {
    // maybe we need another semantic color.text.focused
    return theme.designSystem.color.outline.default
  }
  if (isError) {
    return theme.designSystem.color.border.error
  }
  return theme.designSystem.color.border.default
}

const StyledView = styled(View)<{
  size: InputSize
  isFocused?: boolean
  isError?: boolean
  isDisabled?: boolean
  isHover?: boolean
}>(({ size, isFocused, isError, isDisabled, isHover, theme }) => {
  const heights = SIZE_TO_HEIGHT(theme)
  const borderWidth = isFocused
    ? theme.designSystem.size.spacing.xxs
    : getSpacing(BORDER_FOCUS_INCREASE)
  const horizontalPadding = isFocused
    ? theme.designSystem.size.spacing.xxs
    : theme.designSystem.size.spacing.xxs + BORDER_FOCUS_INCREASE

  const baseBorderColor = getBorderColor(theme, isFocused, isDisabled, isError)

  const borderColor =
    Platform.OS === 'web' && isHover && !isDisabled && !isFocused && !isError
      ? theme.designSystem.color.border.brandPrimary
      : baseBorderColor

  return {
    height: heights[size],
    width: '100%',
    flexDirection: 'row',
    borderStyle: 'solid',
    borderWidth,
    borderColor,
    backgroundColor: isDisabled
      ? theme.designSystem.color.background.disabled
      : theme.designSystem.color.background.default,

    ...padding(0, horizontalPadding),
    alignItems: 'center',
    borderRadius: theme.designSystem.size.borderRadius.m,
    marginTop: theme.designSystem.size.spacing.xs,
  }
})
